/**
 * Vexia GTM Analytics Library
 * A wrapper for GTM GA4 dataLayer that works in both React and WordPress environments
 */

import type {
  AnalyticsConfig,
  DataLayerEvent,
  GA4EventParams,
} from './types';

// Export types for consumers
export type {
  AnalyticsConfig,
  DataLayerEvent,
  GA4EventParams,
};

/**
 * Global configuration
 */
let config: AnalyticsConfig = {
  debug: false,
  dataLayerName: 'dataLayer',
  metadata: {},
};

/**
 * Track if library has been initialized
 */
let isInitialized = false;

/**
 * Initialize the dataLayer if it doesn't exist
 */
function ensureDataLayer(): any[] {
  if (typeof window === 'undefined') {
    return [];
  }

  const layerName = config.dataLayerName || 'dataLayer';
  
  if (!window[layerName as keyof Window]) {
    (window as any)[layerName] = [];
  }

  return (window as any)[layerName];
}

/**
 * Log debug messages if debug mode is enabled
 */
function debugLog(message: string, data?: any): void {
  if (config.debug) {
    console.log(`[Vexia Analytics] ${message}`, data || '');
  }
}

/**
 * Push data to the dataLayer
 * @param data - Data to push to the dataLayer
 */
export function push(data: DataLayerEvent | any): void {
  // Auto-initialize if not already initialized
  if (!isInitialized) {
    init();
  }
  
  const dataLayer = ensureDataLayer();
  
  // Merge metadata with the data being pushed
  const dataWithMetadata = {
    ...config.metadata,
    ...data,
  };
  
  dataLayer.push(dataWithMetadata);
  debugLog('Data pushed to dataLayer:', dataWithMetadata);
}

/**
 * Initialize the analytics library
 * @param options - Configuration options
 */
export function init(options: AnalyticsConfig = {}): void {
  // If already initialized, just update config
  if (isInitialized) {
    if (Object.keys(options).length > 0) {
      const oldConfig = { ...config };
      config = { ...config, ...options };
      debugLog('Analytics re-initialized with updated config:', {
        old: oldConfig,
        new: config,
      });
    } else {
      debugLog('Analytics already initialized, skipping...');
    }
    return;
  }

  // First time initialization
  config = { ...config, ...options };
  
  ensureDataLayer();
  
  isInitialized = true;
  
  debugLog('Analytics initialized with config:', config);
  
  // Expose on window for browser usage
  if (typeof window !== 'undefined') {
    window.VexiaAnalytics = {
      init,
      push,
      event,
      setConfig,
      getConfig,
      isInit,
    };
  }
}

/**
 * Update configuration
 * @param options - Partial configuration options to update
 */
export function setConfig(options: Partial<AnalyticsConfig>): void {
  config = { ...config, ...options };
  debugLog('Config updated:', config);
}

/**
 * Get current configuration
 * @returns Current configuration
 */
export function getConfig(): AnalyticsConfig {
  return { ...config };
}

/**
 * Check if the library has been initialized
 * @returns True if initialized, false otherwise
 */
export function isInit(): boolean {
  return isInitialized;
}

/**
 * Track a custom event
 * @param eventName - Name of the event
 * @param params - Event parameters (optional)
 */
export function event(eventName: string, params: GA4EventParams = {}): void {
  push({
    event: eventName,
    ...params,
  });
}

// Auto-initialize when loaded as a script tag
if (typeof window !== 'undefined' && !(window as any).VexiaAnalytics) {
  init();
}

// Default export for convenience
export default {
  init,
  push,
  event,
  setConfig,
  getConfig,
  isInit,
};
